import { useEffect, useState } from "react";
import MobileSearchBar from "../../ui/MobileSearchBar";
import MobileList from "../../ui/MobileList";
import MobileListItem from "../../ui/MobileListItem";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileError from "../../ui/MobileError";
import { formatarData } from "../../../components/utils/functions";
import { renderGradeValueForTable } from "../../../components/utils/serialGradeFields";
import { useSerialsSearch } from "./useSerialsSearch";
import { MobileSerialDetailSheet } from "./MobileSerialSheets";

function serialSubtitle(serial) {
  const model = serial?.gradableModel;
  const modelParts = [model?.game, model?.edition, model?.name].filter(Boolean).join(" · ");
  const grade = renderGradeValueForTable(
    serial,
    "generalScore",
    serial?.product || serial?.productData
  );
  const gradedAt = serial?.gradedAt ? formatarData(serial.gradedAt) : "";
  return [modelParts, grade ? `Nota: ${grade}` : "", gradedAt].filter(Boolean).join(" · ");
}

export default function MobileSerialsList({ user }) {
  const { serials, loading, error, filters, setFilters, search, refresh } =
    useSerialsSearch(user);
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    setHasSearched(true);
    search();
  }, [search]);

  const handleSearch = (rawQ) => {
    const q = typeof rawQ === "string" ? rawQ : "";
    const next = { ...filters, q };
    setFilters(next);
    setHasSearched(true);
    search(next);
  };

  const openDetail = (serial) => {
    setSelectedSerial(serial);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedSerial(null);
  };

  const handleSerialChanged = () => {
    closeDetail();
    setHasSearched(true);
    refresh();
  };

  const searchLabel = filters.q?.trim() ? "Pesquisar" : "Pesquisar todos";

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={() => {
            setHasSearched(true);
            refresh();
          }}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-primary/50 px-3 text-sm font-medium text-laranjinha/80 hover:bg-monao"
        >
          Atualizar
        </button>
      </div>

      <MobileSearchBar
        value={filters.q}
        onChange={(q) => setFilters((current) => ({ ...current, q }))}
        onSubmit={handleSearch}
        submitLabel={searchLabel}
        placeholder="Serial, nome, jogo ou edição"
      />

      {loading ? (
        <div aria-label="Carregando seriais" role="status">
          <MobileSkeleton lines={5} />
        </div>
      ) : error ? (
        <MobileError
          message={error.message ?? "Erro ao buscar seriais."}
          onRetry={refresh}
        />
      ) : hasSearched && serials.length === 0 ? (
        <MobileEmpty
          title="Nenhum serial encontrado"
          message={
            filters.q?.trim()
              ? `Nenhum resultado para “${filters.q.trim()}”.`
              : "Ajuste a pesquisa ou toque em Pesquisar para buscar."
          }
        />
      ) : serials.length > 0 ? (
        <MobileList>
          {serials.map((item) => (
            <MobileListItem
              key={item._id}
              title={item.serialNumber ?? item._id}
              subtitle={serialSubtitle(item)}
              onClick={() => openDetail(item)}
            />
          ))}
        </MobileList>
      ) : null}

      <MobileSerialDetailSheet
        open={detailOpen}
        onClose={closeDetail}
        serial={selectedSerial}
        user={user}
        onChanged={handleSerialChanged}
      />
    </div>
  );
}
